// Shared portal layout used by advertiser + mall-owner dashboards.
// Provides editorial sidebar (light bone), logout, role-gating.
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { getToken, logout, me } from "@/lib/auth";

export default function PortalLayout({ title, links, allowedRoles, testId }) {
  const nav = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      nav("/login", { replace: true });
      return;
    }
    me()
      .then((u) => {
        if (allowedRoles && !allowedRoles.includes(u.role)) {
          nav("/", { replace: true });
        } else {
          setUser(u);
        }
      })
      .catch(() => { logout(); nav("/login", { replace: true }); });
  }, [nav, allowedRoles]);

  function doLogout() { logout(); nav("/login", { replace: true }); }

  if (!user) return <div className="min-h-screen flex items-center justify-center text-[var(--slate-2)]">Checking session…</div>;

  return (
    <div className="min-h-screen bg-[var(--bone)] flex" data-testid={testId}>
      <aside className="w-64 border-r border-[var(--border)] bg-white hidden lg:flex lg:flex-col">
        <div className="px-6 py-6 border-b border-[var(--border)]">
          <Link to="/" className="flex items-center gap-2">
            <img src="/brand/fundle-logo.png" alt="Fundle" className="h-7 w-auto" />
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] font-semibold mt-2">{title}</div>
          <div className="mt-3 text-xs text-[var(--slate-1)] truncate">{user.name}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)]">{user.email}</div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {links.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-[12px] uppercase tracking-[0.15em] font-medium transition ${
                  isActive ? "bg-[var(--bone-warm)] text-[var(--ink)] border-l-2 border-[var(--gold)]" : "text-[var(--slate-1)] hover:bg-[var(--bone-warm)]"
                }`}>
              <item.icon size={14} strokeWidth={1.6} /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[var(--border)]">
          <Link to="/" target="_blank" className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-[var(--slate-2)] hover:text-[var(--ink)]">
            <ExternalLink size={11} /> View site
          </Link>
          <button onClick={doLogout} data-testid="portal-logout" className="w-full mt-1 flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-[var(--slate-2)] hover:text-[var(--ink)]">
            <LogOut size={11} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 glass-light border-b border-[var(--border)] px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><img src="/brand/fundle-logo.png" alt="" className="h-6 w-auto" /></Link>
        <button onClick={doLogout} className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--gold)]">Sign out</button>
      </div>
      <div className="lg:hidden fixed top-12 left-0 right-0 z-20 bg-white border-b border-[var(--border)] overflow-x-auto no-scrollbar">
        <div className="flex gap-1 px-3 py-2">
          {links.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 font-medium ${isActive ? "text-[var(--gold)] border-b border-[var(--gold)]" : "text-[var(--slate-1)]"}`}>
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-28 lg:pt-0">
        <div className="max-w-[1400px] mx-auto p-6 lg:p-12">
          <Outlet context={{ user }} />
        </div>
      </main>
    </div>
  );
}
